import { Card, CardBody } from "@components/ui/Card"
import { EmptyState } from "@components/ui/EmptyState"
import { effectiveStatus, statusStyle } from "@features/materials"
import { cn } from "@lib/cn"
import { formatRelativeDate } from "@lib/intl"
import { ArrowRight, Sparkles } from "lucide-react"
import { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import { ALL_CLASSROOMS } from "@/constants/classroomScope"
import { MATERIAL_ORIGIN } from "@/constants/materials"
import type { Material } from "@/types"

interface Props {
  materials: Material[]
  onMaterialClick?: (materialId: string, status: string) => void
}

export const HomeRecentMaterials = memo(function HomeRecentMaterials({
  materials,
  onMaterialClick,
}: Props) {
  const { t } = useTranslation(["home", "materials"])
  const navigate = useNavigate()

  const decorated = useMemo(
    () =>
      materials.map((m) => {
        const status = effectiveStatus(m)
        return {
          material: m,
          status,
          style: statusStyle(status),
          when: formatRelativeDate(m.created_at),
          to: `/materiales?status=${status === "borrador_failed" ? "borrador" : status}`,
        }
      }),
    [materials],
  )

  return (
    <Card>
      <CardBody className="space-y-3">
        <h2 className="text-ink text-sm font-semibold">{t("home:recent_materials.title")}</h2>
        {decorated.length === 0 ? (
          <EmptyState
            icon={<Sparkles className="text-ink-subtle h-8 w-8" />}
            title={t("home:recent_materials.empty.title")}
            message={t("home:recent_materials.empty.message")}
            action={
              <Link
                to="/materiales"
                className="text-accent-ink inline-flex items-center gap-1 text-sm font-medium hover:underline"
              >
                {t("home:recent_materials.empty.cta")} <ArrowRight className="h-3 w-3" />
              </Link>
            }
          />
        ) : (
          <>
            <ul className="space-y-2">
              {decorated.map(({ material, status, style, when, to }) => (
                <li key={material.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onMaterialClick?.(material.id, status)
                      navigate(to)
                    }}
                    className="hover:bg-inset dark: flex w-full items-start gap-3 rounded-md p-2 text-left transition-colors focus:outline-none focus-visible:ring-2"
                  >
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                        style.badgeClass,
                      )}
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full", style.dotClass)} />
                      {t(`materials:status.${status}`)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-ink truncate text-sm font-medium">{material.title}</p>
                      <p className="text-ink-subtle truncate text-xs">
                        {material.classroom_name}
                        {material.origin === MATERIAL_ORIGIN.staff ? (
                          <> · {t("materials:origin.staff")}</>
                        ) : null}
                      </p>
                    </div>
                    <span className="text-ink-subtle shrink-0 text-xs">{when}</span>
                  </button>
                </li>
              ))}
            </ul>
            {/* The scope has to be explicit: /materiales with no `classroom`
                param reads as "not chosen yet", and the page then resolves a
                default (localStorage → first classroom, MaterialsPage.tsx:62),
                which landed this link on one arbitrary group. "Ver TODOS" must
                say so in the URL. */}
            <Link
              to={`/materiales?classroom=${ALL_CLASSROOMS}`}
              className="text-accent-ink inline-flex items-center gap-1 text-sm font-medium hover:underline"
            >
              {t("home:recent_materials.view_all")} <ArrowRight className="h-3 w-3" />
            </Link>
          </>
        )}
      </CardBody>
    </Card>
  )
})
